import React, { useEffect, useRef, useState } from "react";
import styles from "./Addupdateaccount.module.css";
import Button from "../Dealers/Button";
import CheckBox from "../FormComponents/CheckBox";
import RadioButtonGroup from "../FormComponents/RadioButtonGroup";

const INITIAL_FORM = {
  acHolderName: "",
  bankName: "",
  accountNumber: "",
  ifscCode: "",
  branchName: "",
  upi: "",
  cart: false,
  pcart: false,
  webCollection: false,
  apiCollection: false,
  status: "Disable",
  qrcode: null,
  qrcodePreview: null,
};

const INITIAL_ERRORS = {
  acHolderName: "",
  bankName: "",
  accountNumber: "",
  ifscCode: "",
  branchName: "",
  upi: "",
  type: "", // at least one checkbox must be selected
};

// ─── Validation rules ──────────────────────────────────────────────────────────
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const UPI_REGEX = /^[\w.\-]{2,256}@[a-zA-Z]{2,64}$/;
const AC_NUM_REGEX = /^\d{9,18}$/;
const NAME_REGEX = /^[a-zA-Z\s.'-]{2,100}$/;

const validate = (form) => {
  const errors = { ...INITIAL_ERRORS };
  let isValid = true;

  // Safe trim — converts numbers to string first, returns "" only for booleans/null/undefined
  const s = (val) =>
    val == null || typeof val === "boolean" ? "" : String(val).trim();

  // A/C Holder Name
  if (!s(form.acHolderName)) {
    errors.acHolderName = "Account holder name is required";
    isValid = false;
  } else if (!NAME_REGEX.test(s(form.acHolderName))) {
    errors.acHolderName = "Enter a valid name (letters only, min 2 characters)";
    isValid = false;
  }

  // Bank Name
  if (!s(form.bankName)) {
    errors.bankName = "Bank name is required";
    isValid = false;
  } else if (s(form.bankName).length < 3) {
    errors.bankName = "Bank name must be at least 3 characters";
    isValid = false;
  }

  // Account Number
  if (!s(form.accountNumber)) {
    errors.accountNumber = "Account number is required";
    isValid = false;
  } else if (!AC_NUM_REGEX.test(s(form.accountNumber))) {
    errors.accountNumber = "Enter a valid account number (9-18 digits)";
    isValid = false;
  }

  // IFSC Code
  if (!s(form.ifscCode)) {
    errors.ifscCode = "IFSC code is required";
    isValid = false;
  } else if (!IFSC_REGEX.test(s(form.ifscCode).toUpperCase())) {
    errors.ifscCode = "Enter a valid IFSC code (e.g. SBIN0001234)";
    isValid = false;
  }

  // Branch Name
  if (!s(form.branchName)) {
    errors.branchName = "Branch name is required";
    isValid = false;
  } else if (s(form.branchName).length < 2) {
    errors.branchName = "Branch name must be at least 2 characters";
    isValid = false;
  }

  // UPI Id
  if (!s(form.upi)) {
    errors.upi = "UPI ID is required";
    isValid = false;
  } else if (!UPI_REGEX.test(s(form.upi))) {
    errors.upi = "Enter a valid UPI ID (e.g. name@upi)";
    isValid = false;
  }

  // At least one type must be selected
  const anyType =
    form.cart || form.pcart || form.webCollection || form.apiCollection;
  if (!anyType) {
    errors.type = "Select at least one account type";
    isValid = false;
  }

  return { errors, isValid };
};

const AddUpdateAccount = ({
  mode,
  isOpen,
  onClose,
  editData = null,
  onSendOtp,
}) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState(INITIAL_ERRORS);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef();

  const isUpdate = Boolean(editData);

  // ─── Prefill form when opening in update mode ──────────────────────────────
  useEffect(() => {
    if (editData) {
      setForm({
        acHolderName: editData.acHolderName || "",
        bankName: editData.bankName || "",
        accountNumber:
          editData.accountNumber != null ? String(editData.accountNumber) : "",
        ifscCode: editData.ifscCode || "",
        branchName: editData.branchName || "",
        upi: editData.upi || "",
        cart: editData.cart === "Active",
        pcart: editData.pcart === "Active",
        webCollection: editData.webCollection === "Active",
        apiCollection: editData.apiCollection === "Active",
        status: editData.status || "Disable",
        qrcode: null,
        qrcodePreview: editData.qrcode
          ? process.env.REACT_APP_S3URL + editData.qrcode
          : null,
      });
    } else {
      setForm(INITIAL_FORM);
    }
    // Clear errors whenever the modal opens/closes or switches between add/edit
    setErrors(INITIAL_ERRORS);
  }, [editData, isOpen]);

  if (!isOpen) return null;

  // ─── Generic field setter — clears that field's error on change ───────────
  const set = (key, val) => {
    setForm((prev) => ({ ...prev, [key]: val }));
    // Clear the error for this field as soon as the user starts correcting it
    if (key in errors) {
      setErrors((prev) => ({ ...prev, [key]: "" }));
    }
    // Clear type error when any checkbox changes
    if (["cart", "pcart", "webCollection", "apiCollection"].includes(key)) {
      setErrors((prev) => ({ ...prev, type: "" }));
    }
  };

  // ─── QR file handling ──────────────────────────────────────────────────────
  const handleFile = (file) => {
    if (!file) return;
    set("qrcode", file);
    set("qrcodePreview", URL.createObjectURL(file));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  // ─── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const { errors: validationErrors, isValid } = validate(form);

    if (!isValid) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);

      const payload = {
        ...(isUpdate && { _id: editData._id }),
        acHolderName: form.acHolderName.trim(),
        bankName: form.bankName.trim(),
        accountNumber: form.accountNumber.trim(),
        ifscCode: form.ifscCode.trim().toUpperCase(),
        branchName: form.branchName.trim(),
        upi: form.upi.trim(),
        cart: form.cart ? "Active" : "Disable",
        pcart: form.pcart ? "Active" : "Disable",
        webCollection: form.webCollection ? "Active" : "Disable",
        apiCollection: form.apiCollection ? "Active" : "Disable",
        status: form.status,
        qrcode: form.qrcode,
      };

      await onSendOtp(payload, isUpdate);
      onClose();
    } catch (err) {
      console.error("AddUpdateAccount handleSubmit error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    // <div className={styles.overlay} onClick={onClose}>
    //------------------for outside click not close modal ------------------
    <div className={styles.overlay}>
      <div
        className={`${styles.modal} ${mode ? styles.dark : styles.light}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Title bar ── */}
        <div className={styles.titleBar}>
          <span className={styles.title}>
            {isUpdate ? "Update Bank Account" : "Add Bank Account"}
          </span>
          <button className={styles.closeBtn} onClick={onClose}>
            ✕
          </button>
        </div>

        <div className={styles.body}>
          {/* ── Row 1: A/C Holder + Bank ── */}
          <div className={styles.row}>
            <Field
              label="A/C Holder Name"
              error={errors.acHolderName}
              mode={mode}
            >
              <input
                className={`${styles.input} ${errors.acHolderName ? styles.inputError : ""}`}
                placeholder="Enter A/C Holder Name"
                value={form.acHolderName}
                onChange={(e) => set("acHolderName", e.target.value)}
              />
            </Field>

            <Field label="Bank Name" error={errors.bankName} mode={mode}>
              <input
                className={`${styles.input} ${errors.bankName ? styles.inputError : ""}`}
                placeholder="Enter Bank Name"
                value={form.bankName}
                onChange={(e) => set("bankName", e.target.value)}
              />
            </Field>
          </div>

          {/* ── Row 2: Account Number + IFSC ── */}
          <div className={styles.row}>
            <Field
              label="Account Number"
              error={errors.accountNumber}
              mode={mode}
            >
              <input
                className={`${styles.input} ${errors.accountNumber ? styles.inputError : ""}`}
                placeholder="Enter Account Number"
                inputMode="numeric"
                pattern="[0-9]*"
                value={form.accountNumber}
                onChange={(e) => {
                  // Strip any non-digit characters (blocks letters on physical keyboards too)
                  const onlyDigits = e.target.value.replace(/\D/g, "");
                  set("accountNumber", onlyDigits);
                }}
              />
            </Field>

            <Field label="IFSC Code" error={errors.ifscCode} mode={mode}>
              <input
                className={`${styles.input} ${errors.ifscCode ? styles.inputError : ""}`}
                placeholder="e.g. SBIN0001234"
                value={form.ifscCode}
                onChange={(e) => set("ifscCode", e.target.value.toUpperCase())}
              />
            </Field>
          </div>

          {/* ── Row 3: Branch + UPI ── */}
          <div className={styles.row}>
            <Field label="Branch Name" error={errors.branchName} mode={mode}>
              <input
                className={`${styles.input} ${errors.branchName ? styles.inputError : ""}`}
                placeholder="Enter Branch Name"
                value={form.branchName}
                onChange={(e) => set("branchName", e.target.value)}
              />
            </Field>

            <Field label="UPI Id" error={errors.upi} mode={mode}>
              <input
                className={`${styles.input} ${errors.upi ? styles.inputError : ""}`}
                placeholder="e.g. name@upi"
                value={form.upi}
                onChange={(e) => set("upi", e.target.value)}
              />
            </Field>
          </div>

          {/* ── Type checkboxes + Status radio ── */}
          <div className={styles.typeStatusRow}>
            <div className={styles.typeGroup}>
              <span className={styles.groupLabel}>
                Type
                {errors.type && (
                  <span className={styles.typeError}>
                    &nbsp;— {errors.type}
                  </span>
                )}
              </span>
              <div className={styles.checkboxRow}>
                {[
                  { key: "cart", label: "Cart" },
                  { key: "pcart", label: "Partner Cart" },
                  { key: "webCollection", label: "Web Collection" },
                  { key: "apiCollection", label: "API Collection" },
                ].map(({ key, label }) => (
                  <div key={key} className={styles.checkbox_sec}>
                    <CheckBox
                      checked={form[key]}
                      handleChange={(e) => set(key, e.target.checked)}
                    />
                    <p style={{ color: mode ? "#ffffff" : undefined }}>
                      {label}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.statusGroup}>
              <span className={styles.groupLabel}>Status</span>
              <RadioButtonGroup
                key={form.status}
                options={[
                  { label: "Active", value: "Active" },
                  { label: "Disable", value: "Disable" },
                ]}
                selectedOption={form.status}
                onChange={(value) => set("status", value)}
              />
            </div>
          </div>

          {/* ── QR Code Upload ── */}
          <div
            className={`${styles.dropZone} ${dragOver ? styles.dragActive : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current.click()}
          >
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={(e) => handleFile(e.target.files[0])}
            />

            {form.qrcodePreview ? (
              <div className={styles.previewWrapper}>
                <img
                  src={form.qrcodePreview}
                  alt="QR Preview"
                  className={styles.qrPreview}
                />
                <button
                  className={styles.removeImg}
                  onClick={(e) => {
                    e.stopPropagation();
                    set("qrcode", null);
                    set("qrcodePreview", null);
                  }}
                >
                  ✕
                </button>
              </div>
            ) : (
              <>
                <div className={styles.uploadIcon}>
                  <svg
                    width="36"
                    height="36"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <rect x="3" y="3" width="7" height="7" rx="1" />
                    <rect x="14" y="3" width="7" height="7" rx="1" />
                    <rect x="3" y="14" width="7" height="7" rx="1" />
                    <rect x="14" y="14" width="7" height="7" rx="1" />
                  </svg>
                </div>
                <p className={styles.dropText}>Drag QR photo here</p>
                <div className={styles.orDivider}>
                  <span className={styles.orLine} />
                  <span className={styles.orWord}>OR</span>
                  <span className={styles.orLine} />
                </div>
                <button
                  className={styles.browseBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    fileRef.current.click();
                  }}
                >
                  Browse Photos
                </button>
              </>
            )}
          </div>
        </div>

        {/* ── Footer ── */}
        <div className={styles.footer}>
          <Button
            buttonText="Cancel"
            buttonBgColor="#000"
            buttonTextColor="#fff"
            buttonClick={onClose}
          />
          <Button
            buttonBgColor="#4164E3"
            buttonTextColor="#fff"
            buttonText={loading ? "Sending OTP..." : "Next"}
            className={styles.submitBtn}
            buttonClick={handleSubmit}
            disabled={loading}
          />
        </div>
      </div>
    </div>
  );
};

// ─── Field wrapper — shows label + inline error below the input ───────────────
const Field = ({ label, error, children }) => (
  <div style={{ flex: 1, minWidth: 0 }}>
    <label
      style={{
        display: "block",
        fontSize: 13,
        marginBottom: 6,
        fontWeight: 500,
      }}
    >
      {label}
    </label>
    {children}
    {error && (
      <p style={{ margin: "4px 0 0", fontSize: 11, color: "#E53935" }}>
        {error}
      </p>
    )}
  </div>
);

export default AddUpdateAccount;
